import { useToast } from "@chakra-ui/react";
import { format } from "date-fns";
import CDS from "design-system";

import FormFillView from "features/Resources/CustomForm/Components/FormFillView";
import {
  useCustomFormQuery,
  useCustomFormResponseForTaskQuery,
  useSubmitCustomFormResponse,
  customFormKeys,
} from "features/Resources/CustomForm/hooks";
import type { CustomFormAnswerEntry } from "features/Resources/CustomForm/types";
import { useQueryClient } from "@tanstack/react-query";
import type { Task } from "features/Resources/Task/models";

interface Props {
  task: Task;
  onClose?: () => void;
}

export function CompleteFormTaskActionForm({ task, onClose }: Props) {
  const { customFormId } = task;
  const { data: form, isLoading: formLoading, isError: formError } = useCustomFormQuery(customFormId ?? undefined);
  const { data: existingResponse, isLoading: responseLoading } = useCustomFormResponseForTaskQuery(task.id);
  const { mutate: submit, isPending } = useSubmitCustomFormResponse(task.id);
  const toast = useToast();
  const queryClient = useQueryClient();

  const submitAnswers = (answers: CustomFormAnswerEntry[]) => {
    submit(
      { answers },
      {
        onSuccess: () => {
          toast({ title: "Form submitted", status: "success", duration: 3000 });
          onClose?.();
        },
        onError: (error: unknown) => {
          const status = (error as { status?: number })?.status;
          if (status === 409) {
            toast({
              title: "Already submitted",
              description: "This form was already submitted for this task.",
              status: "warning",
              duration: 5000,
            });
            queryClient.invalidateQueries({ queryKey: customFormKeys.responseForTask(task.id) });
          } else {
            toast({
              title: "Submission failed",
              description: "Please try again.",
              status: "error",
              duration: 5000,
            });
          }
        },
      },
    );
  };

  if (!customFormId) {
    return <CDS.Text14 color="red.500">This task doesn&apos;t reference a form.</CDS.Text14>;
  }

  if (formLoading || responseLoading) {
    return <CDS.Text14>Loading…</CDS.Text14>;
  }

  // Answers are never displayed on task surfaces — authorized viewers see them
  // in the form's Responses tab.
  if (existingResponse) {
    return (
      <CDS.Text14 color="fg.subtle">
        This form was submitted on {format(new Date(existingResponse.submittedAt), "MMM d, yyyy 'at' h:mm a")}.
      </CDS.Text14>
    );
  }

  // Form 404s when the admin deletes/archives it after the task was created.
  if (formError || !form) {
    return (
      <CDS.Text14 color="fg.subtle">
        This form is no longer available. Ask your admin to reassign this task.
      </CDS.Text14>
    );
  }

  return <FormFillView form={form} onSubmit={submitAnswers} isSubmitting={isPending} onCancel={onClose} />;
}
